import React, { useState } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert,
  Table
} from "react-bootstrap";
import api from "../../api";

function CreateOrder() {
  const [platform, setPlatform] = useState("Mercari");
  const [externalId, setExternalId] = useState("");
  const [sales, setSales] = useState("");
  const [fees, setFees] = useState("");
  const [shipping, setShipping] = useState("");

  const [skus, setSkus] = useState([
    {
      value: "",
      quantity: 1,
      cost: 0,
      name: "",
      image: "",
      results: []
    }
  ]);

  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const baseUrl =
    process.env.REACT_APP_API_URL || "https://thornsdashboard.com";

  const getImageUrl = (image) => {
    if (!image) return "";
    return image.startsWith("http")
      ? image
      : `${baseUrl}${image}`;
  };

  const addSkuField = () => {
    setSkus([
      ...skus,
      { value: "", quantity: 1, cost: 0, name: "", image: "", results: [] }
    ]);
  };

  const removeSkuField = (index) => {
    // Always keep at least 1 row
    if (skus.length === 1) {
      setSkus([{ value: "", quantity: 1, cost: 0, name: "", image: "", results: [] }]);
      return;
    }
    const updated = [...skus];
    updated.splice(index, 1);
    setSkus(updated);
  };

  const updateSku = async (index, value) => {
    const updated = [...skus];
    updated[index].value = value;

    // If user edits text after selecting, clear filled details
    updated[index].name = "";
    updated[index].image = "";
    updated[index].cost = 0;

    if (value.length > 1) {
      try {
        const res = await api.get(
          `/products?search=${encodeURIComponent(value)}&limit=5`
        );
        updated[index].results = res.data.products || [];
      } catch {
        updated[index].results = [];
      }
    } else {
      updated[index].results = [];
    }

    setSkus(updated);
  };

  const selectSku = (index, product) => {
    const updated = [...skus];
    updated[index] = {
      ...updated[index],
      value: product.sku,
      name: product.name,
      image: product.image,
      cost: Number(product.cost_of_goods || 0),
      results: []
    };
    setSkus(updated);
  };

  const updateQuantity = (index, qty) => {
    const updated = [...skus];
    const n = Number(qty);
    updated[index].quantity = Number.isFinite(n) && n >= 1 ? n : 1;
    setSkus(updated);
  };

  const submitOrder = async () => {
    try {
      setSubmitting(true);
      setError(null);
      setMessage(null);

      // Basic front-end validation
      if (!platform || !externalId.trim()) {
        setError("Platform and External Order ID are required.");
        return;
      }

      const items = skus
        .filter((s) => s.value.trim() !== "")
        .map((s) => ({
          sku: s.value.trim(),
          quantity: Number(s.quantity) || 1,
          cost_of_goods: Number(s.cost) || 0
        }));

      if (!items.length) {
        setError("Add at least 1 SKU item.");
        return;
      }

      const payload = {
        platform,
        external_order_id: externalId.trim(),
        sales: Number(sales) || 0,
        fees: Number(fees) || 0,
        shipping: shipping ? Number(shipping) : 0,
        items
      };

      // ✅ This matches your backend route you shared: router.post('/create', ...)
      // So the frontend MUST call /tools/create-external-order/create
      await api.post("/external/create", payload);



      setMessage("Order created successfully!");
      setExternalId("");
      setSales("");
      setFees("");
      setShipping("");
      setSkus([
        { value: "", quantity: 1, cost: 0, name: "", image: "", results: [] }
      ]);
    } catch (err) {
      console.error(err);
      const serverMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to create order";
      setError(serverMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const totalCost = skus.reduce(
    (sum, s) => sum + (Number(s.cost) || 0) * (Number(s.quantity) || 0),
    0
  );

  return (
    <Container className="p-4 text-light">
      <h3 className="fw-bold">Create External Order</h3>

      <Card
        className="p-4 mt-3"
        style={{
          backgroundColor: "#1e1e2f",
          border: "1px solid #2e2e40",
          color: "#fff"
        }}
      >
        <Form>
          <Row>
            <Col md={4}>
              <Form.Group>
                <Form.Label>Platform</Form.Label>
                <Form.Select
                  className="bg-dark text-light border-secondary"
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  disabled={submitting}
                >
                  <option>Mercari</option>
                  <option>Poshmark</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={8}>
              <Form.Group>
                <Form.Label>External Order ID</Form.Label>
                <Form.Control
                  className="bg-dark text-light border-secondary"
                  value={externalId}
                  onChange={(e) => setExternalId(e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Sales (Gross)</Form.Label>
                <Form.Control
                  className="bg-dark text-light border-secondary"
                  type="number"
                  step="0.01"
                  value={sales}
                  onChange={(e) => setSales(e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Platform Fees</Form.Label>
                <Form.Control
                  className="bg-dark text-light border-secondary"
                  type="number"
                  step="0.01"
                  value={fees}
                  onChange={(e) => setFees(e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group>
                <Form.Label>Shipping Cost</Form.Label>
                <Form.Control
                  className="bg-dark text-light border-secondary"
                  type="number"
                  step="0.01"
                  value={shipping}
                  onChange={(e) => setShipping(e.target.value)}
                  disabled={submitting}
                />
              </Form.Group>
            </Col>
          </Row>

          <hr className="border-secondary" />
          <h5>Order Items</h5>

          {skus.map((skuObj, index) => (
            <div key={index} style={{ position: "relative", marginBottom: 15 }}>
              <Row className="g-2">
                <Col md={5}>
                  <Form.Control
                    className="bg-dark text-light border-secondary"
                    placeholder="Search SKU..."
                    value={skuObj.value}
                    onChange={(e) => updateSku(index, e.target.value)}
                    disabled={submitting}
                  />

                  {skuObj.results.length > 0 && !submitting && (
                    <div
                      style={{
                        position: "absolute",
                        background: "#26263a",
                        width: "100%",
                        border: "1px solid #444",
                        zIndex: 1000,
                        maxHeight: 250,
                        overflowY: "auto",
                        borderRadius: 6,
                        marginTop: 2
                      }}
                    >
                      {skuObj.results.map((p) => (
                        <div
                          key={p.sku}
                          style={{
                            padding: 10,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            borderBottom: "1px solid #333"
                          }}
                          onClick={() => selectSku(index, p)}
                        >
                          {p.image ? (
                            <img
                              src={getImageUrl(p.image)}
                              alt=""
                              style={{
                                width: 40,
                                height: 40,
                                objectFit: "cover",
                                borderRadius: 4
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 40,
                                height: 40,
                                borderRadius: 4,
                                background: "#151525",
                                border: "1px solid #333"
                              }}
                            />
                          )}

                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 600 }}>
                              {p.sku}
                            </div>
                            <div
                              style={{
                                fontSize: 13,
                                color: "#bbb",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: 320
                              }}
                            >
                              {p.name}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Col>

                <Col md={2}>
                  <Form.Control
                    className="bg-dark text-light border-secondary"
                    type="number"
                    min="1"
                    value={skuObj.quantity}
                    onChange={(e) => updateQuantity(index, e.target.value)}
                    disabled={submitting}
                  />
                </Col>

                <Col md={2}>
                  <Form.Control
                    className="bg-dark text-light border-secondary"
                    value={Number(skuObj.cost).toFixed(2)}
                    readOnly
                  />
                </Col>

                <Col md={2} className="d-flex align-items-center fw-bold">
                  ${(Number(skuObj.cost) * Number(skuObj.quantity)).toFixed(2)}
                </Col>

                <Col md={1} className="d-flex align-items-center justify-content-end">
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => removeSkuField(index)}
                    disabled={submitting}
                    title="Remove item"
                  >
                    ✕
                  </Button>
                </Col>
              </Row>

              {/* Optional: show selected item preview under the row */}
              {skuObj.name ? (
                <div style={{ marginTop: 6, color: "#bdbdd1", fontSize: 13 }}>
                  <span style={{ opacity: 0.9 }}>
                    Selected: <strong>{skuObj.value}</strong>
                  </span>{" "}
                  — {skuObj.name}
                </div>
              ) : null}
            </div>
          ))}

          <div className="d-flex gap-2">
            <Button
              variant="outline-light"
              onClick={addSkuField}
              disabled={submitting}
            >
              + Add Item
            </Button>
          </div>

          <hr className="border-secondary" />

          <Table
            bordered
            size="sm"
            className="text-light"
            style={{ borderColor: "#444" }}
          >
            <tbody>
              <tr>
                <td>Total Cost of Goods</td>
                <td>${totalCost.toFixed(2)}</td>
              </tr>
            </tbody>
          </Table>

          <Button variant="primary" onClick={submitOrder} disabled={submitting}>
            {submitting ? "Creating..." : "Create Order"}
          </Button>
        </Form>

        {message && (
          <Alert variant="success" className="mt-3">
            {message}
          </Alert>
        )}
        {error && (
          <Alert variant="danger" className="mt-3">
            {error}
          </Alert>
        )}
      </Card>
    </Container>
  );
}

export default CreateOrder;
